import { Typography, Box, Divider } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { MeetingDetailsOrder } from "../../../../../../nonAdminUser/reports/home/meetingServiceApi"
import DeliveryDetails from "./DeliveryDetails"
import ContactDetails from "./ContactDetails"
import OrderItemsTable from "../slipModal/OrderItemsTable"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useMemo } from "react"

const useStyles = makeStyles()((theme) => ({
    orderContainer: {
        "&:last-child": {
            marginBottom: 0,
        },
        "@media print": {
            breakAfter: "page",
            "&:last-child": {
                breakAfter: "auto",
            },
            marginBottom: 0,
            breakInside: "avoid",
        },
    },
    orderDivider: {
        borderStyle: "dashed",
        borderColor: theme.palette.divider,
    },
    orderId: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[700],
        marginBottom: theme.spacing(0.5),
    },
    mainContent: {
        display: "flex",
        gap: theme.spacing(4),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
        },
        "@media print": {
            gap: theme.spacing(2),
            display: "flex",
            flexDirection: "row",
            breakInside: "avoid",
        },
    },
    leftColumn: {
        flex: "0 0 40%",
        maxWidth: "40%",
        "@media print": {
            flex: "0 0 50%",
            maxWidth: "50%",
            breakInside: "avoid",
        },
    },
    rightColumn: {
        flex: `0 0 calc(60% - ${theme.spacing(4)})`,
        maxWidth: `calc(60% - ${theme.spacing(4)})`,
        "@media print": {
            flex: `0 0 calc(50% - ${theme.spacing(2)})`,
            maxWidth: `calc(50% - ${theme.spacing(2)})`,
            breakInside: "avoid",
        },
    },
    orderName: {
        fontWeight: 400,
        fontSize: "1.125rem",
        color: theme.palette.tailwind.slate[500],
    },
}))

interface SingleOrderProps {
    order: MeetingDetailsOrder
    itemIndex?: number
    totalOrders: number
}

export default function SingleOrder({ order, itemIndex = 0, totalOrders }: SingleOrderProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const deliveryTypeLabel = useMemo(() => {
        return l10n.getStringForEnum("delivery-type-filter", order.deliveryDetails.type, order.deliveryDetails.type)
    }, [order.deliveryDetails.type, l10n])

    const mapTableOrders = useMemo(() => {
        return order.deliveries.flatMap((delivery) =>
            delivery.orderLines.map((orderLine) => ({
                id: orderLine.id,
                items: orderLine.items,
                name: orderLine.name,
                deliveryTime: orderLine.deliveryTime,
            })),
        )
    }, [order])

    return (
        <>
            <Box className={classes.orderContainer}>
                <Box className={classes.mainContent}>
                    <Box className={classes.leftColumn}>
                        <Box>
                            <Typography variant="h6" className={classes.orderId}>
                                {deliveryTypeLabel}:{" "}
                                {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(order.deliveryDetails.time))}
                            </Typography>
                            <Typography variant="body1" className={classes.orderName}>
                                #{order?.id} - {order?.meetingDetails?.meetingName}
                            </Typography>
                        </Box>
                        <DeliveryDetails order={order} />
                        <ContactDetails order={order} />
                    </Box>

                    <Box className={classes.rightColumn}>
                        <OrderItemsTable orders={mapTableOrders} />
                    </Box>
                </Box>
            </Box>

            {itemIndex < totalOrders - 1 && <Divider className={classes.orderDivider} />}
        </>
    )
}
